import { Delete } from '@mui/icons-material';
import {
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { EntityType } from '../../util/enums';
import { BACKGROUND_OFFSET, PRIMARY } from '../../Style/GeoMobyBaseTheme';
import { Property } from '../ProjectConfig/types';
import { SelectProperty } from './SelectProperty';
import { SelectValue } from './SelectValue';
import { Fragment, ReactNode } from 'react';
import { VOID } from 'ol/functions';
import { PropertyType } from '../ProjectConfig/enums';
import styles from './../../Common/truncate.module.css';
import { useAtomValue } from 'jotai';
import { EMISSIONS_RATE_UNIT } from '../../store/map';
import {
  asEmissionRateUnitOrDefault,
  displayEmissionRateUnitByUnit,
} from '../Map/LiveAndReplay/Sidebar/Beacon/helpers';
import { getAssetTypeTitle } from '../Map/Helpers';
import { PRODUCTION_CAPACITY_PROPERTY_KEYS } from '../Assets/helpers';

const ROW_HEIGHT = 10;

export const searchPropertyName = (
  geomobyProperties: Record<string, string>,
  searchPropertyName: string,
) => {
  return Object.entries(geomobyProperties).find(([property, _]) =>
    property.toLocaleLowerCase().includes(searchPropertyName.toLocaleLowerCase()),
  );
};

export const searchPropertyValue = (
  geomobyProperties: Record<string, string>,
  searchPropertyValue: string,
) => {
  return Object.entries(geomobyProperties).find(([_, value]) =>
    value.toLocaleLowerCase().includes(searchPropertyValue.toLocaleLowerCase()),
  );
};

export const GeomobyPropertiesDisplayTable = ({
  rows,
}: {
  rows: { index: number; property: string; value: string }[];
}) => (
  <TableContainer
    component={Paper}
    sx={{
      width: '100%',
      overflowX: 'auto',
    }}
  >
    <Table
      stickyHeader
      size="small"
      sx={{
        tableLayout: 'fixed',
        minWidth: 'max-content',
      }}
    >
      <TableHead>
        <TableRow sx={{ height: ROW_HEIGHT }}>
          <TableCell
            style={{
              width: 50,
              minWidth: 50,
              backgroundColor: BACKGROUND_OFFSET,
            }}
          >
            Property
          </TableCell>
          <TableCell
            style={{
              width: 50,
              minWidth: 50,
              backgroundColor: BACKGROUND_OFFSET,
            }}
            align="right"
          >
            Value
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 && (
          <TableRow>
            <TableCell>No properties found</TableCell>
            <TableCell></TableCell>
          </TableRow>
        )}
        {rows
          .sort((a, b) => a.property.localeCompare(b.property))
          .map(row => (
            <TableRow
              key={row.property}
              sx={{
                height: ROW_HEIGHT,
                '&:last-child td, &:last-child th': { border: 0 },
              }}
            >
              <TableCell sx={{ width: 200, minWidth: 200 }}>
                <Typography className={styles.truncateUnlessHover}>{row.property}</Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: 200, minWidth: 200 }}>
                <Typography className={styles.truncateUnlessHover}>{row.value}</Typography>
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export const GeomobyPropertiesEditTable = ({
  entityType,
  disableAddButton,
  displayGeomobyProperties,
  allProperties,
  setOnHitEnter,
  onChangeProperty,
  onBlurValue,
  onDelete,
  onAdd,
}: {
  entityType?: EntityType;
  disableAddButton: boolean;
  displayGeomobyProperties: { index: number; property: string; value: string }[];
  allProperties: Property[];
  setOnHitEnter: (hitEnter: boolean) => void;
  onChangeProperty: (
    property: string,
    prop: { index: number; property: string; value: string },
  ) => Promise<void>;
  onBlurValue: (
    value: string,
    prop: { index: number; property: string; value: string },
  ) => Promise<void>;
  onDelete: (property: { index: number; property: string; value: string }) => Promise<void>;
  onAdd: () => void;
}) => {
  const onKeyPress = (event: { key: string }) => {
    if (event.key !== 'Enter') return;
    setOnHitEnter(true);
  };

  return (
    <>
      <Grid
        container
        style={{
          marginTop: '10px',
        }}
      >
        {entityType && (
          <Typography style={{ color: PRIMARY }}>{`${getAssetTypeTitle(
            entityType,
          )} Properties`}</Typography>
        )}

        <TableContainer
          component={Paper}
          style={{
            maxHeight: '500px',
          }}
        >
          <Table stickyHeader size="small" aria-label="a dense table">
            <TableHead>
              <TableRow
                key={'header-row'}
                style={{
                  height: '55px',
                }}
              >
                <TableCell
                  key={'Property'}
                  style={{ backgroundColor: BACKGROUND_OFFSET }}
                  align="left"
                >
                  Property
                </TableCell>
                <TableCell
                  key={'Value'}
                  style={{ backgroundColor: BACKGROUND_OFFSET }}
                  align="left"
                >
                  Value
                </TableCell>
                <TableCell
                  key={''}
                  style={{ backgroundColor: BACKGROUND_OFFSET }}
                  align="left"
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayGeomobyProperties
                .sort((a, b) => {
                  return String(a.index).localeCompare(String(b.index));
                })
                .map(property => {
                  const existingProperty = allProperties.find(p => p.label === property.property);
                  const isListProperty = existingProperty?.type === PropertyType.List;
                  const isNumberProperty = existingProperty?.type === PropertyType.Number;
                  const isEmissionsRateProperty = property.property === 'emissions_rate';
                  const isInvalidNumberValue = isNumberProperty && isNaN(Number(property.value));
                  const isInvalidListValue =
                    isListProperty &&
                    !existingProperty?.items?.find(item => item === property.value);
                  const isInvalidProperty = !existingProperty && !!property.property;

                  return (
                    <Fragment key={`${property.property}-${property.value}-${property.index}`}>
                      <TableRow
                        key={`${property.index}-row`}
                        sx={{
                          height: '55px',
                          '&:last-child td, &:last-child th': { border: 0 },
                          ...(isInvalidProperty || isInvalidListValue || isInvalidNumberValue
                            ? {
                                '& td, & th': {
                                  borderBottom: 'none',
                                },
                              }
                            : {}),
                        }}
                      >
                        {/* Property */}
                        <TableCell
                          key={`${property.index}-property-cell`}
                          style={{
                            padding: '6px 0px 3px 5px',
                            width: '40%',
                            maxWidth: '190px',
                            verticalAlign: 'top',
                          }}
                        >
                          <SelectProperty
                            property={property}
                            allProperties={allProperties}
                            isFromTable={true}
                            displayGeomobyProperties={displayGeomobyProperties}
                            onKeyPress={onKeyPress}
                            onChangeProperty={onChangeProperty}
                          />
                        </TableCell>

                        {/* Value */}
                        <TableCell
                          key={`${property.index}-value-cell`}
                          style={{
                            padding: '6px 0px 3px 5px',
                            width: '40%',
                            verticalAlign: 'top',
                          }}
                        >
                          <SelectValue
                            property={property}
                            allProperties={allProperties}
                            isFromTable={true}
                            onKeyPress={onKeyPress}
                            onChangeValue={VOID}
                            onBlurValue={onBlurValue}
                          />
                        </TableCell>

                        {/* Delete */}
                        <TableCell
                          key={`${property.index}-delete`}
                          style={{
                            padding: '0px 5px 0px 0px',
                            width: '10%',
                          }}
                        >
                          <IconButton
                            color="primary"
                            disabled={isEmissionsRateProperty}
                            onClick={() => onDelete(property)}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow
                        sx={{
                          '& td, & th': {
                            borderTop: 'none',
                          },
                        }}
                      >
                        {(isInvalidProperty || isInvalidListValue || isInvalidNumberValue) && (
                          <TableCell style={{ paddingBottom: '5px', paddingTop: 0 }} colSpan={6}>
                            <Typography
                              style={{
                                fontSize: 'small',
                                marginLeft: '-10px',
                                color: '#F44336',
                              }}
                            >
                              {isInvalidListValue || isInvalidNumberValue
                                ? 'INVALID VALUE. Please update.'
                                : 'INVALID. Either delete here or create in Project Configuration.'}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    </Fragment>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Button
          variant="outlined"
          style={{
            marginTop: '10px',
            width: '100%',
          }}
          disabled={disableAddButton}
          onClick={onAdd}
        >
          Add
        </Button>
      </Grid>
    </>
  );
};

export const GeomobyPropertiesEditForm = ({
  disableAddButton,
  displayGeomobyProperties,
  allProperties,
  setOnHitEnter,
  onChangeValue,
  onChangeProperty,
  onBlurValue,
  onDelete,
  onAdd,
  children,
}: {
  disableAddButton: boolean;
  displayGeomobyProperties: { index: number; property: string; value: string }[];
  allProperties: Property[];
  setOnHitEnter: (hitEnter: boolean) => void;
  onChangeValue: (value: string, prop: { index: number; property: string; value: string }) => void;
  onChangeProperty: (
    property: string,
    prop: { index: number; property: string; value: string },
  ) => Promise<void>;
  onBlurValue: (
    value: string,
    prop: { index: number; property: string; value: string },
  ) => Promise<void>;
  onDelete: (property: { index: number; property: string; value: string }) => Promise<void>;
  onAdd: () => void;
  children?: ReactNode;
}) => {
  const emissionsRateUnit = useAtomValue(EMISSIONS_RATE_UNIT);
  const onKeyPress = (event: { key: string }) => {
    if (event.key !== 'Enter') return;
    setOnHitEnter(true);
  };

  const getPropertyAdornment = (property: { index: number; property: string; value: string }) => {
    if (property.property === 'emissions_rate') {
      return displayEmissionRateUnitByUnit[asEmissionRateUnitOrDefault(emissionsRateUnit)];
    }
  };

  return (
    <Grid
      container
      style={{
        marginTop: '10px',
      }}
    >
      {displayGeomobyProperties
        .sort((a, b) => {
          return String(a.index).localeCompare(String(b.index));
        })
        .map(property => {
          const isHiddenProperty =
            property.property === 'emissions_unit' ||
            PRODUCTION_CAPACITY_PROPERTY_KEYS.has(property.property);
          const isDisabled = property.property === 'emissions_rate';
          if (isHiddenProperty) return;

          return (
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              key={`${property.index}-row`}
              marginBottom={2}
            >
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                key={`${property.index}-row`}
                marginBottom={2}
                width={'90%'}
              >
                {/* Property */}
                <SelectProperty
                  property={property}
                  allProperties={allProperties}
                  displayGeomobyProperties={displayGeomobyProperties}
                  onKeyPress={onKeyPress}
                  onChangeProperty={onChangeProperty}
                />

                {/* Value */}
                <SelectValue
                  property={property}
                  allProperties={allProperties}
                  adornment={getPropertyAdornment(property)}
                  onKeyPress={onKeyPress}
                  onChangeValue={onChangeValue}
                  onBlurValue={onBlurValue}
                />
              </Grid>

              {/* Delete */}
              <IconButton
                key={`${property.index}-delete`}
                color="primary"
                disabled={isDisabled}
                style={{
                  height: 'fit-content',
                  marginTop: '5px',
                }}
                onClick={() => onDelete(property)}
              >
                <Delete />
              </IconButton>
            </Grid>
          );
        })}

      {children}

      <Button
        variant="outlined"
        style={{
          marginTop: '10px',
          width: '100%',
        }}
        disabled={disableAddButton}
        onClick={onAdd}
      >
        Add
      </Button>
    </Grid>
  );
};
